import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  InfoCircleOutlined,
  CompassOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  AdminTour,
  TourType,
  TourSlide,
  CreateTourPayload,
  UpdateTourPayload,
} from '../lib/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const KNOWN_INTRO_TOUR_KEYS = [
  'HOMEPAGE_OVERVIEW',
  'ADD_ITEMS',
  'TRY_ON',
  'CLOSET_OVERVIEW',
  'PHOTO_MODELS',
  'AI_STYLIST',
  'QUICK_ACTIONS',
  'NAVIGATION',
  'ADD_CLOTH',
  'PROFILE',
];

const INTRO_TOUR_KEY_LABELS: Record<string, string> = {
  HOMEPAGE_OVERVIEW: '🏠 Homepage Overview',
  ADD_ITEMS: '➕ Add Items',
  TRY_ON: '👗 Try-On',
  CLOSET_OVERVIEW: '👚 Closet Overview',
  PHOTO_MODELS: '📷 Photo Models',
  AI_STYLIST: '🤖 AI Stylist',
  QUICK_ACTIONS: '⚡ Quick Actions',
  NAVIGATION: '🧭 Navigation',
  ADD_CLOTH: '🧥 Add Cloth',
  PROFILE: '👤 Profile',
};

const SLIDE_CTA_ACTIONS = [
  { value: 'next', label: 'Next slide' },
  { value: 'dismiss', label: 'Dismiss tour' },
  { value: 'redirect', label: 'Redirect to page' },
];

// ---------------------------------------------------------------------------
// Slide editor row (local state, no Ant Form field for complex list)
// ---------------------------------------------------------------------------

interface SlideRow extends TourSlide {
  _key: number;
}

let _slideKeyCounter = 0;

const SlideEditor: React.FC<{ value: SlideRow[]; onChange: (rows: SlideRow[]) => void }> = ({
  value,
  onChange,
}) => {
  const addSlide = () => {
    onChange([
      ...value,
      {
        _key: ++_slideKeyCounter,
        slide_id: `slide_${value.length + 1}`,
        order: value.length,
        title: '',
        body: '',
        image_url: null,
        cta_label: 'Next',
        cta_action: 'next',
        cta_url: null,
      },
    ]);
  };

  const removeSlide = (key: number) => {
    onChange(value.filter((s) => s._key !== key));
  };

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...value];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next.map((s, i) => ({ ...s, order: i })));
  };

  const moveDown = (idx: number) => {
    if (idx >= value.length - 1) return;
    const next = [...value];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next.map((s, i) => ({ ...s, order: i })));
  };

  const updateSlide = (key: number, patch: Partial<TourSlide>) => {
    onChange(value.map((s) => (s._key === key ? { ...s, ...patch } : s)));
  };

  return (
    <div>
      {value.length === 0 && (
        <Empty
          description="No slides yet. Add your first slide."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ marginBottom: 16 }}
        />
      )}

      {value.map((slide, idx) => (
        <Card
          key={slide._key}
          size="small"
          style={{ marginBottom: 12, background: '#fafafa', borderColor: '#d9d9d9' }}
          title={
            <Space>
              <Tag color="blue">Slide {idx + 1}</Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {slide.slide_id}
              </Text>
            </Space>
          }
          extra={
            <Space>
              <Button
                size="small"
                type="text"
                icon={<ArrowUpOutlined />}
                disabled={idx === 0}
                onClick={() => moveUp(idx)}
              />
              <Button
                size="small"
                type="text"
                icon={<ArrowDownOutlined />}
                disabled={idx === value.length - 1}
                onClick={() => moveDown(idx)}
              />
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeSlide(slide._key)}
              />
            </Space>
          }
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Slide ID" style={{ marginBottom: 8 }}>
                <Input
                  size="small"
                  value={slide.slide_id}
                  onChange={(e) => updateSlide(slide._key, { slide_id: e.target.value })}
                  placeholder="e.g., slide_1"
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item label="Title" style={{ marginBottom: 8 }}>
                <Input
                  size="small"
                  value={slide.title ?? ''}
                  onChange={(e) => updateSlide(slide._key, { title: e.target.value || null })}
                  placeholder="Slide headline"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Body" style={{ marginBottom: 8 }}>
            <TextArea
              size="small"
              rows={2}
              value={slide.body ?? ''}
              onChange={(e) => updateSlide(slide._key, { body: e.target.value || null })}
              placeholder="Slide body text"
            />
          </Form.Item>

          <Form.Item label="Image URL (optional)" style={{ marginBottom: 8 }}>
            <Input
              size="small"
              value={slide.image_url ?? ''}
              onChange={(e) => updateSlide(slide._key, { image_url: e.target.value || null })}
              placeholder="https://..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="CTA Label" style={{ marginBottom: 8 }}>
                <Input
                  size="small"
                  value={slide.cta_label ?? ''}
                  onChange={(e) => updateSlide(slide._key, { cta_label: e.target.value || null })}
                  placeholder="e.g., Next"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="CTA Action" style={{ marginBottom: 8 }}>
                <Select
                  size="small"
                  value={slide.cta_action ?? undefined}
                  onChange={(v) => updateSlide(slide._key, { cta_action: v })}
                  allowClear
                  placeholder="next / dismiss / redirect"
                  style={{ width: '100%' }}
                >
                  {SLIDE_CTA_ACTIONS.map((a) => (
                    <Option key={a.value} value={a.value}>
                      {a.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              {slide.cta_action === 'redirect' && (
                <Form.Item label="Redirect URL" style={{ marginBottom: 8 }}>
                  <Input
                    size="small"
                    value={slide.cta_url ?? ''}
                    onChange={(e) => updateSlide(slide._key, { cta_url: e.target.value || null })}
                    placeholder="/subscription"
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
        </Card>
      ))}

      <Button type="dashed" block icon={<PlusOutlined />} onClick={addSlide}>
        Add Slide
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TourForm
// ---------------------------------------------------------------------------

interface TourFormProps {
  initialValues?: AdminTour;
  onSubmit: (payload: CreateTourPayload | UpdateTourPayload) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const TourForm: React.FC<TourFormProps> = ({ initialValues, onSubmit, onCancel, isLoading }) => {
  const [form] = Form.useForm();
  const isEditing = !!initialValues;

  // Tour type is fixed on create; cannot change after creation
  const [tourType, setTourType] = useState<TourType>(initialValues?.tour_type ?? 'intro');
  const ctaAction = Form.useWatch('claim_bottomsheet_cta_action', form);
  const skippable = Form.useWatch('skippable', form);

  // Slides (managed outside Ant Form)
  const [slides, setSlides] = useState<SlideRow[]>(() =>
    (initialValues?.slides ?? []).map((s) => ({ ...s, _key: ++_slideKeyCounter }))
  );

  React.useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        tour_id: initialValues.tour_id,
        title: initialValues.title,
        description: initialValues.description,
        is_active: initialValues.is_active ?? true,
        skippable: initialValues.skippable ?? true,
        intro_skip_label: initialValues.intro_skip_label,
        intro_done_label: initialValues.intro_done_label,
        intro_next_label: initialValues.intro_next_label,
        intro_prev_label: initialValues.intro_prev_label,
        intro_show_step_numbers: initialValues.intro_show_step_numbers ?? true,
        show_progress_dots: initialValues.show_progress_dots ?? true,
        completion_redirect_url: initialValues.completion_redirect_url,
      });
    } else {
      form.setFieldsValue({
        is_active: true,
        skippable: true,
        intro_show_step_numbers: true,
        show_progress_dots: true,
      });
    }
  }, [initialValues, form]);

  const handleFinish = async (values: any) => {
    const base = {
      title: values.title,
      description: values.description || null,
      is_active: values.is_active,
      skippable: values.skippable,
    };

    if (isEditing) {
      const payload: UpdateTourPayload = {
        ...base,
        ...(tourType === 'intro'
          ? {
              intro_skip_label: values.intro_skip_label || null,
              intro_done_label: values.intro_done_label || null,
              intro_next_label: values.intro_next_label || null,
              intro_prev_label: values.intro_prev_label || null,
              intro_show_step_numbers: values.intro_show_step_numbers,
            }
          : {
              slides: slides.map(({ _key, ...rest }) => rest),
              show_progress_dots: values.show_progress_dots,
              completion_redirect_url: values.completion_redirect_url || null,
            }),
      };
      await onSubmit(payload);
    } else {
      const payload: CreateTourPayload = {
        ...base,
        tour_id: values.tour_id,
        tour_type: tourType,
        ...(tourType === 'intro'
          ? {
              intro_skip_label: values.intro_skip_label || null,
              intro_done_label: values.intro_done_label || null,
              intro_next_label: values.intro_next_label || null,
              intro_prev_label: values.intro_prev_label || null,
              intro_show_step_numbers: values.intro_show_step_numbers,
            }
          : {
              slides: slides.map(({ _key, ...rest }) => rest),
              show_progress_dots: values.show_progress_dots,
              completion_redirect_url: values.completion_redirect_url || null,
            }),
      };
      await onSubmit(payload);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} requiredMark="optional">
      <Title level={5} style={{ marginBottom: 16 }}>
        {isEditing ? `Edit Tour: ${initialValues?.tour_id}` : 'Create New Tour'}
      </Title>

      {/* Tour Type Selector — only shown on create */}
      {!isEditing && (
        <>
          <Divider orientation="left">Tour Type</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Card
                hoverable
                onClick={() => setTourType('intro')}
                style={{
                  borderColor: tourType === 'intro' ? '#1890ff' : '#d9d9d9',
                  background: tourType === 'intro' ? '#e6f7ff' : undefined,
                  cursor: 'pointer',
                }}
                bodyStyle={{ padding: 16 }}
              >
                <Space direction="vertical" size={4}>
                  <Space>
                    <CompassOutlined style={{ color: '#1890ff', fontSize: 20 }} />
                    <Text strong>Intro Tour</Text>
                    {tourType === 'intro' && <Tag color="blue">Selected</Tag>}
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Controls an existing frontend guided tour (intro.js). You manage its
                    active/inactive state, skippability, and button labels. Steps live in the
                    frontend code.
                  </Text>
                </Space>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                hoverable
                onClick={() => setTourType('slide')}
                style={{
                  borderColor: tourType === 'slide' ? '#722ed1' : '#d9d9d9',
                  background: tourType === 'slide' ? '#f9f0ff' : undefined,
                  cursor: 'pointer',
                }}
                bodyStyle={{ padding: 16 }}
              >
                <Space direction="vertical" size={4}>
                  <Space>
                    <PlayCircleOutlined style={{ color: '#722ed1', fontSize: 20 }} />
                    <Text strong>Slide Tour</Text>
                    {tourType === 'slide' && <Tag color="purple">Selected</Tag>}
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    A fully admin-created slide carousel. You define each slide's content,
                    images, and CTA buttons. No frontend code changes needed.
                  </Text>
                </Space>
              </Card>
            </Col>
          </Row>
          <div style={{ marginBottom: 24 }} />
        </>
      )}

      {!isEditing && tourType === 'intro' && (
        <Alert
          type="info"
          style={{ marginBottom: 16 }}
          showIcon
          message="Intro Tour ID must match a known frontend TourKey"
          description="Select one of the predefined tour keys below. These map to existing guided tours in the app's frontend code."
        />
      )}

      {/* Basic Info */}
      <Divider orientation="left">Basic Information</Divider>

      {/* Tour ID */}
      {!isEditing && (
        <Form.Item
          name="tour_id"
          label="Tour ID"
          rules={[{ required: true, message: 'Tour ID is required' }]}
          extra={
            tourType === 'intro'
              ? 'Must match a known frontend TourKey exactly'
              : 'Custom slug, e.g. "gold_features_tour"'
          }
        >
          {tourType === 'intro' ? (
            <Select placeholder="Select a frontend TourKey">
              {KNOWN_INTRO_TOUR_KEYS.map((key) => (
                <Option key={key} value={key}>
                  {INTRO_TOUR_KEY_LABELS[key] || key}
                </Option>
              ))}
            </Select>
          ) : (
            <Input
              placeholder="e.g., gold_features_tour"
            />
          )}
        </Form.Item>
      )}

      <Form.Item
        name="title"
        label="Title"
        rules={[{ required: true, message: 'Title is required' }]}
        extra="Human-readable name shown in admin panel"
      >
        <Input placeholder="e.g., Gold Features Tour" maxLength={100} />
      </Form.Item>

      <Form.Item name="description" label="Admin Notes (optional)">
        <TextArea placeholder="Purpose of this tour, usage notes..." rows={2} maxLength={300} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="skippable"
            label={
              <Space>
                Skippable
                <Tooltip title="When off, the user cannot dismiss the tour until they finish all steps/slides.">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </Space>
            }
            valuePropName="checked"
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Col>
      </Row>

      {/* ── Intro-specific fields ── */}
      {tourType === 'intro' && (
        <>
          <Divider orientation="left">Label Overrides</Divider>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
            Leave blank to use frontend defaults. Filled values override the hardcoded button labels.
          </Text>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="intro_skip_label"
                label="Skip Button Label"
                extra='Default: "Skip Tour"'
              >
                <Input placeholder="Skip Tour" maxLength={40} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="intro_done_label"
                label="Done Button Label"
                extra='Default: "Done"'
              >
                <Input placeholder="Done" maxLength={40} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="intro_next_label"
                label="Next Button Label"
                extra='Default: "Next"'
              >
                <Input placeholder="Next" maxLength={40} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="intro_prev_label"
                label="Previous Button Label"
                extra='Default: "Previous"'
              >
                <Input placeholder="Previous" maxLength={40} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="intro_show_step_numbers"
            label="Show Step Numbers"
            valuePropName="checked"
          >
            <Switch checkedChildren="Shown" unCheckedChildren="Hidden" />
          </Form.Item>
        </>
      )}

      {/* ── Slide-specific fields ── */}
      {tourType === 'slide' && (
        <>
          <Divider orientation="left">Slide Settings</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="show_progress_dots" label="Show Progress Dots" valuePropName="checked">
                <Switch checkedChildren="Shown" unCheckedChildren="Hidden" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="completion_redirect_url"
                label="Auto-redirect After Last Slide"
                extra="Optional. Navigates to this route when the tour finishes."
              >
                <Input placeholder="/subscription" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Slides</Divider>
          <SlideEditor value={slides} onChange={setSlides} />
        </>
      )}

      <Divider />

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEditing ? 'Update Tour' : 'Create Tour'}
          </Button>
          <Button onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

// ---------------------------------------------------------------------------
// Tours page
// ---------------------------------------------------------------------------

const Tours: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTour, setEditingTour] = useState<AdminTour | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'all' | 'intro' | 'slide'>('all');
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin-tours', activeTab],
    queryFn: () =>
      adminApi.getTours(
        activeTab !== 'all' ? { tour_type: activeTab as TourType } : undefined
      ),
    staleTime: 60 * 1000,
  });

  const tours = data?.tours ?? [];

  const createMutation = useMutation({
    mutationFn: (payload: CreateTourPayload) => adminApi.createTour(payload),
    onSuccess: () => {
      message.success('Tour created successfully!');
      setDrawerOpen(false);
      setEditingTour(undefined);
      queryClient.invalidateQueries({ queryKey: ['admin-tours'] });
    },
    onError: (err: any) => {
      message.error(`Failed to create tour: ${err.response?.data?.detail || err.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ tourId, payload }: { tourId: string; payload: UpdateTourPayload }) =>
      adminApi.updateTour(tourId, payload),
    onSuccess: () => {
      message.success('Tour updated successfully!');
      setDrawerOpen(false);
      setEditingTour(undefined);
      queryClient.invalidateQueries({ queryKey: ['admin-tours'] });
    },
    onError: (err: any) => {
      message.error(`Failed to update tour: ${err.response?.data?.detail || err.message}`);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ tourId, isActive }: { tourId: string; isActive: boolean }) =>
      adminApi.updateTour(tourId, { is_active: isActive }),
    onSuccess: (_, { isActive }) => {
      message.success(`Tour ${isActive ? 'activated' : 'deactivated'}`);
      queryClient.invalidateQueries({ queryKey: ['admin-tours'] });
    },
    onError: (err: any) => {
      message.error(`Failed to update tour: ${err.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (tourId: string) => adminApi.deleteTour(tourId),
    onSuccess: () => {
      message.success('Tour deleted');
      queryClient.invalidateQueries({ queryKey: ['admin-tours'] });
    },
    onError: (err: any) => {
      message.error(`Failed to delete tour: ${err.message}`);
    },
  });

  const handleFormSubmit = async (payload: CreateTourPayload | UpdateTourPayload) => {
    if (editingTour) {
      await updateMutation.mutateAsync({
        tourId: editingTour.tour_id,
        payload: payload as UpdateTourPayload,
      });
    } else {
      await createMutation.mutateAsync(payload as CreateTourPayload);
    }
  };

  const handleEdit = (tour: AdminTour) => {
    setEditingTour(tour);
    setDrawerOpen(true);
  };

  const columns = [
    {
      title: 'Tour',
      key: 'tour',
      render: (_: any, record: AdminTour) => (
        <Space direction="vertical" size={2}>
          <Space>
            <Text strong>{record.title}</Text>
            <Tag color={record.tour_type === 'intro' ? 'blue' : 'purple'}>
              {record.tour_type === 'intro' ? (
                <><CompassOutlined /> Intro</>
              ) : (
                <><PlayCircleOutlined /> Slide</>
              )}
            </Tag>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            ID: <code>{record.tour_id}</code>
          </Text>
          {record.description && (
            <Text style={{ fontSize: 12, color: '#666' }}>{record.description}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Details',
      key: 'details',
      width: 220,
      render: (_: any, record: AdminTour) => {
        if (record.tour_type === 'intro') {
          const overrides = [
            record.intro_skip_label && `Skip: "${record.intro_skip_label}"`,
            record.intro_done_label && `Done: "${record.intro_done_label}"`,
            record.intro_next_label && `Next: "${record.intro_next_label}"`,
            record.intro_prev_label && `Prev: "${record.intro_prev_label}"`,
          ].filter(Boolean);
          return (
            <Space direction="vertical" size={2}>
              {overrides.length > 0 ? (
                overrides.map((o, i) => (
                  <Text key={i} style={{ fontSize: 11 }} type="secondary">
                    {o}
                  </Text>
                ))
              ) : (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  No label overrides
                </Text>
              )}
              {!record.intro_show_step_numbers && (
                <Tag style={{ fontSize: 10 }}>Step numbers hidden</Tag>
              )}
            </Space>
          );
        }
        // slide
        return (
          <Space direction="vertical" size={2}>
            <Text style={{ fontSize: 12 }}>
              {record.slides?.length ?? 0} slide{(record.slides?.length ?? 0) !== 1 ? 's' : ''}
            </Text>
            {record.completion_redirect_url && (
              <Text style={{ fontSize: 11 }} type="secondary">
                → {record.completion_redirect_url}
              </Text>
            )}
            {!record.show_progress_dots && (
              <Tag style={{ fontSize: 10 }}>No progress dots</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Skippable',
      key: 'skippable',
      width: 100,
      render: (_: any, record: AdminTour) => (
        <Tag color={record.skippable ? 'green' : 'orange'}>
          {record.skippable ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Active',
      key: 'is_active',
      width: 90,
      render: (_: any, record: AdminTour) => (
        <Switch
          checked={record.is_active}
          size="small"
          loading={toggleActiveMutation.isPending}
          onChange={(checked) =>
            toggleActiveMutation.mutate({ tourId: record.tour_id, isActive: checked })
          }
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      render: (_: any, record: AdminTour) => (
        <Space>
          <Tooltip title="Edit Tour">
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this tour?"
            description="This will soft-delete the tour. It can be restored later."
            onConfirm={() => deleteMutation.mutate(record.tour_id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete Tour">
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deleteMutation.isPending}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const introTours = tours.filter((t) => t.tour_type === 'intro');
  const slideTours = tours.filter((t) => t.tour_type === 'slide');

  return (
    <div className="page-content" style={{ padding: 24 }}>
      {/* Header */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Title level={2} style={{ margin: 0 }}>
            <CompassOutlined style={{ marginRight: 8 }} />
            Tour Management
          </Title>
          <Text type="secondary">
            Manage guided tours — both frontend intro.js tours and fully admin-created slide tours
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingTour(undefined);
                setDrawerOpen(true);
              }}
            >
              New Tour
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Stats row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff' }}>
                {tours.length}
              </div>
              <Text type="secondary">Total Tours</Text>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
                {tours.filter((t) => t.is_active).length}
              </div>
              <Text type="secondary">Active</Text>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff' }}>
                {introTours.length}
              </div>
              <Text type="secondary">Intro Tours</Text>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#722ed1' }}>
                {slideTours.length}
              </div>
              <Text type="secondary">Slide Tours</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {error && (
        <Alert
          type="error"
          message="Failed to load tours"
          description="Check your backend connection."
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Intro tours explainer */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        icon={<CompassOutlined />}
        message={
          <Space>
            <Text strong>Intro Tours</Text>
            <Tag color="blue">Frontend-powered</Tag>
          </Space>
        }
        description={
          <Text style={{ fontSize: 12 }}>
            Intro tours use <strong>intro.js</strong> in the frontend. Their steps are defined in
            code. Here you can toggle them on/off, override button labels, and control
            skippability. Valid IDs:{' '}
            {KNOWN_INTRO_TOUR_KEYS.map((k) => (
              <Tag key={k} style={{ marginBottom: 2, fontSize: 10 }}>
                {k}
              </Tag>
            ))}
          </Text>
        }
      />

      {/* Table */}
      <Card bodyStyle={{ padding: 0 }}>
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as any)}
          tabBarStyle={{ paddingLeft: 16, paddingRight: 16 }}
          items={[
            {
              key: 'all',
              label: `All (${tours.length})`,
              children: (
                <div style={{ padding: '0 0 16px' }}>
                  {isLoading ? (
                    <Spin style={{ display: 'block', margin: '40px auto' }} />
                  ) : (
                    <Table
                      dataSource={tours}
                      columns={columns}
                      rowKey="tour_id"
                      pagination={{ pageSize: 20 }}
                      size="middle"
                      scroll={{ x: 800 }}
                      rowClassName={(r) => (!r.is_active ? 'row-inactive' : '')}
                    />
                  )}
                </div>
              ),
            },
            {
              key: 'intro',
              label: (
                <Space>
                  <CompassOutlined />
                  Intro ({introTours.length})
                </Space>
              ),
              children: (
                <div style={{ padding: '0 0 16px' }}>
                  {isLoading ? (
                    <Spin style={{ display: 'block', margin: '40px auto' }} />
                  ) : (
                    <Table
                      dataSource={introTours}
                      columns={columns}
                      rowKey="tour_id"
                      pagination={false}
                      size="middle"
                      scroll={{ x: 800 }}
                      rowClassName={(r) => (!r.is_active ? 'row-inactive' : '')}
                    />
                  )}
                </div>
              ),
            },
            {
              key: 'slide',
              label: (
                <Space>
                  <PlayCircleOutlined />
                  Slide ({slideTours.length})
                </Space>
              ),
              children: (
                <div style={{ padding: '0 0 16px' }}>
                  {isLoading ? (
                    <Spin style={{ display: 'block', margin: '40px auto' }} />
                  ) : (
                    <Table
                      dataSource={slideTours}
                      columns={columns}
                      rowKey="tour_id"
                      pagination={false}
                      size="middle"
                      scroll={{ x: 800 }}
                      rowClassName={(r) => (!r.is_active ? 'row-inactive' : '')}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Create / Edit Drawer */}
      <Drawer
        title={editingTour ? `Edit Tour: ${editingTour.title}` : 'Create New Tour'}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingTour(undefined);
        }}
        width={760}
        destroyOnClose
      >
        <TourForm
          initialValues={editingTour}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setDrawerOpen(false);
            setEditingTour(undefined);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Drawer>
    </div>
  );
};

export default Tours;

